import React, {useContext, useEffect, useState} from 'react';
import {IconButton, Tooltip} from '@mui/material';
import Stack from '@mui/material/Stack';
import {
    generateImageDetectionsId,
    isMobile,
    matchesAnyRegex,
    parseJSON,
    rotateImageFile
} from "../App/utils.js";
import {imageToTextAPI} from "../API/ImageToTextAPI.js";
import {MESSAGE} from "../App/constants.js";
import Dropzone from '../Chat/Dropzone.js';
import * as EXIF from "exif-js";
import {AlertContext} from "../App/App.js";
import {inputTools} from "./constant.js";
import {uploadFileAPI} from "../API/KB/Files.js";
import {kbUserData, RAW_FILES_URL} from "../utils";

const types = [
    "image/jpeg",
    "image/pjpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/tiff",
    "image/svg+xml",
    "image/x-icon"
];

function renameFile(originalFile, newName, newExt) {
    return new File([originalFile], newName, {
        type: newExt,
        lastModified: originalFile.lastModified
    });
}

export function getKBImageURL(filename, kbId) {
    kbId = kbId || kbUserData()?.kbId;
    return `${RAW_FILES_URL}/files/${kbId}/${filename}`
}

export function createOnImageToTextResponse({setBlockingLoading, kbId, imageFiles, setImageFile, messages, RequestChatAPI}) {
    return async (detections, file, isRotated = false) => {
        if (file === undefined) return;
        
        const imageDetectionId = generateImageDetectionsId(detections);

        const nextImageFiles = {
            ...imageFiles,
            [imageDetectionId]: file
        };

        setImageFile(nextImageFiles);

        uploadFileAPI(renameFile(file, imageDetectionId + '.' + 'jpeg', 'jpeg'),
            'files', kbId, (percentage) => {});

        const imageUrl = getKBImageURL(imageDetectionId + '.' + 'jpeg', kbId);
        const message = { type: MESSAGE.TEXT_DETECTIONS, imageUrl, detections};


        if (isRotated) message.isRotated = isRotated;

        const content = JSON.stringify(message);

        setBlockingLoading(false);

        await RequestChatAPI([...messages, { role: 'user', content}], {nextImageFiles});
    }
}

export function onLevelerImageRotation({KB, chatMessages, nextImageFiles, imageFiles, onImageToTextResponse, leveler, setSystemAlert}) {
    const prevJSONMessage = chatMessages[chatMessages.length - 2]
        ? parseJSON(chatMessages[chatMessages.length - 2].content)
        : null;

    if (!prevJSONMessage || !prevJSONMessage.detections) {
        setSystemAlert({msg: 'Unable to find image to be rotated', type: 'error', duration: 3000})
        return;
    }

    const imageFile = nextImageFiles[ generateImageDetectionsId(prevJSONMessage.detections) ]
        || imageFiles [ generateImageDetectionsId(prevJSONMessage.detections) ]

    imageFile.leveler = leveler;

    if (imageFile) {
        rotateImageFile(imageFile).then(rotatedFile => {
            imageToTextAPI(KB, rotatedFile).then((response) => {
                // Here we have recursion to RequestChatAPI
                onImageToTextResponse(response.detections, rotatedFile, true);
            });
        })
    }
}

export const RotatingImage = ({ imageFile, leveler, onManualImageRotation, kbId, detections }) => {
    const [angle, setAngle] = useState(0);

    const imageUrl = imageFile
        ? URL.createObjectURL(imageFile)
        : getKBImageURL(generateImageDetectionsId(detections) + '.' + 'jpeg', kbId);

    useEffect(() => {
        if (!leveler) return;
        const [ A, B ] = leveler;
        let theta = Math.atan2(B.y - A.y, B.x - A.x);
        theta *= -(180 / Math.PI);


        EXIF.getData(imageFile, function() {
            const orientation = EXIF.getTag(this, "Orientation");

            switch (orientation) {
                case 3:
                    theta -= 180;
                    break;
                case 6:
                    theta -= 90;
                    break;
                case 8:
                    theta -= 270;
                    break;
            }

            setAngle(theta);
        });
    }, [leveler]);

    const style = leveler ? {
        transform: `rotate(${angle}deg)`,
        // Change this to the actual point A
        transformOrigin: leveler ? `${leveler.x}px ${leveler.y}px` : 'top left',
        transition: 'transform 1s',
        transitionDelay: '0.2s',
    } : {};

    return <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
                src={imageUrl}
                alt="Uploaded File"
                style={{
                    maxWidth: isMobile ? '100%' : '50%',
                    maxHeight: isMobile ? '100%' : '50%',
                    ...style
                }}
            />
        </div>
};

export const ImageToText = ({ style, setBlockingLoading, onImageToTextResponse, isRotated = false, textOnly, KB }) => {
    const {setSystemAlert} = useContext(AlertContext);
    const onFileUpload = async event => {
        const selectedFile = event instanceof File ? event : event.target.files[0];
        if (!selectedFile?.type) return;

        if (!matchesAnyRegex(selectedFile.type, types)) {
            setSystemAlert({msg: 'Invalid file type', type: 'error', duration: 3000})
            return;
        }


        if (!selectedFile) {
            if (!isRotated) setSystemAlert({msg: 'Please select an image to upload', type: 'error', duration: 3000})
            return;
        }

        try {
            setBlockingLoading(true)
            const response = await imageToTextAPI(KB, selectedFile, textOnly);
            await onImageToTextResponse(response.detections, selectedFile, isRotated);
        } catch (e) {
            setBlockingLoading(false)
            setSystemAlert({msg: 'Error during upload', type: 'error', duration: 3000})
            console.error(e);
        }
        if(event?.target?.value) event.target.value = null;
    }

    return (
        <Stack alignItems="center" spacing={2} style={style} >
            <Dropzone onFileUpload={onFileUpload} types={types} id={"imageToText"} />

            <Tooltip title={textOnly ? inputTools.imageToText.label : inputTools.imageToTextAdvanced.label}>
                <IconButton color="primary" aria-label="upload picture" component="label">
                    <input hidden accept="image/*" type="file" onChange={onFileUpload} />
                    {textOnly
                        ? (isMobile ? inputTools.imageToText.iconMobile() : inputTools.imageToText.icon())
                        : (isMobile ? inputTools.imageToTextAdvanced.iconMobile() : inputTools.imageToTextAdvanced.icon())
                    }

                </IconButton>
            </Tooltip>
        </Stack>
    );
}